import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import type { PageConfig } from '~/types';

interface QueryParamConfig {
  type?: string;
  default?: string | number | boolean;
}

export function usePageQueryParams(pageConfig: PageConfig | null | undefined) {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const queryParamsConfig = (pageConfig?.queryParams || {}) as Record<string, QueryParamConfig>;
    const params: Record<string, string | number | boolean | null> = {};

    for (const [key, config] of Object.entries(queryParamsConfig)) {
      const value = searchParams.get(key);

      if (value === null) {
        // Use default if provided
        params[key] = config.default !== undefined ? config.default : null;
      } else {
        // Parse based on type
        switch (config.type) {
          case 'number':
            params[key] = parseInt(value, 10) || (config.default as number) || 0;
            break;
          case 'boolean':
            params[key] = value === 'true' || value === '1';
            break;
          case 'string':
          default:
            params[key] = value;
            break;
        }
      }
    }

    return params;
  }, [searchParams, pageConfig?.queryParams]);
}

export function useUpdateQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  return (updates: Record<string, string | number | boolean | null | undefined>) => {
    const newParams = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    }

    setSearchParams(newParams);
  };
}
